import { Component, computed, effect, input, signal } from '@angular/core'

import { DialogModule } from 'primeng/dialog'

import { ProductVariantSize, PriceRange } from 'app/_shared/interfaces/ProductVariant'
import { UsdPipe } from 'app/_shared/pipes/currency.pipe'
import { PricePipe } from 'app/_shared/pipes/price.pipe'
import { sortPriceRanges, formatRangeLabel } from 'app/_shared/services/pricing.utils'

interface PriceRangeRow extends PriceRange {
  label: string
  savingsPercent: number | null
  isBestValue: boolean
}

@Component({
  selector: 'app-bulk-pricing-dialog',
  standalone: true,
  imports: [DialogModule, PricePipe, UsdPipe],
  templateUrl: './bulk-pricing-dialog.component.html',
  styleUrl: './bulk-pricing-dialog.component.scss'
})
export class BulkPricingDialogComponent {
  sizes = input<ProductVariantSize[]>([])
  selectedSize = input<string>('')

  visible = signal(false)
  activeSize = signal<string>('')

  constructor() {
    effect(() => {
      const sel = this.selectedSize()
      if (sel) {
        this.activeSize.set(sel)
      } else if (this.sizes().length) {
        this.activeSize.set(this.sizes()[0].size)
      }
    })
  }

  activeSizeObj = computed<ProductVariantSize | undefined>(() =>
    this.sizes().find(s => s.size === this.activeSize())
  )

  rows = computed<PriceRangeRow[]>(() => {
    const sizeObj = this.activeSizeObj()
    if (!sizeObj?.price_ranges?.length) return []

    const sorted = sortPriceRanges(sizeObj.price_ranges)
    const basePrice = sizeObj.price && sizeObj.price > 0 ? sizeObj.price : (sorted[0]?.price ?? 0)

    const bestPrice = Math.min(...sorted.map(range => range.price))

    return sorted.map(range => ({
      ...range,
      label: formatRangeLabel(range.min_quantity, range.max_quantity),
      savingsPercent:
        basePrice <= 0 || range.price >= basePrice
          ? null
          : Math.round(((basePrice - range.price) / basePrice) * 100),
      isBestValue: sorted.length > 1 && range.price === bestPrice
    }))
  })
  open(): void {
    this.visible.set(true)
  }

  close(): void {
    this.visible.set(false)
  }

  selectSize(size: string): void {
    this.activeSize.set(size)
  }
}
